import React, { useState, useRef, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { fetchHolidays, fetchAppointments } from "../api";
import {
  AREA_COLORS, PRIO_ORDER, MONTHS_SHORT, WEEKDAYS,
  APPT_TEMPLATE_PATTERN, APPT_VACATION_BLOCK_PATTERN,
  APPT_INDIVIDUAL_VACATION_PATTERN, APPT_OFFICE_PATTERN,
} from "../constants";
import { getMonday, addDays, getISOWeek, formatISODate as formatDate, parseISODateLocal, useToday } from "../lib/dates";
import PrioIcon from "./PrioIcon";
import TaskQuickMenu from "./TaskQuickMenu";
import ConfirmDialog from "./ConfirmDialog";
import FilterBar from "./FilterBar";
import MiniCalendar from "./MiniCalendar";
import "./Calendar.css";

const WEEKDAYS_MF = WEEKDAYS.slice(0, 5);

function formatDateShort(d) {
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
}

/* "Komplett löschen" for a vacation/Betriebsferien block: same text alone is
   not enough (two "Betriebsferien" blocks in one year must not delete each
   other), and the loaded calendar range may clip the block. So the days are
   fetched fresh around the clicked date and only the CONTIGUOUS run counts —
   gaps of up to 3 days bridge weekends that have no entry. */
async function collectBlockDays(appointment) {
  const center = parseISODateLocal(appointment.date);
  let pool = [];
  try {
    pool = await fetchAppointments(formatDate(addDays(center, -120)), formatDate(addDays(center, 120)));
  } catch { /* fall back to just the clicked day below */ }
  const sameText = (pool || []).filter((t) => !t.outlook_id && t.date && t.text === appointment.text);
  if (!sameText.some((t) => t.id === appointment.id)) sameText.push(appointment);
  const byDate = sameText.sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id);
  const idx = byDate.findIndex((t) => t.id === appointment.id);
  const gapDays = (a, b) => Math.round((parseISODateLocal(b) - parseISODateLocal(a)) / 86400000);
  const run = [byDate[idx]];
  for (let i = idx - 1; i >= 0; i--) {
    if (gapDays(byDate[i].date, run[0].date) <= 3) run.unshift(byDate[i]); else break;
  }
  for (let i = idx + 1; i < byDate.length; i++) {
    if (gapDays(run[run.length - 1].date, byDate[i].date) <= 3) run.push(byDate[i]); else break;
  }
  return run;
}

function isPast(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const parts = dateStr.split("-");
  const d = new Date(+parts[0], +parts[1] - 1, +parts[2]);
  return d < today;
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

function getWeeksFromStart(startMonday, count) {
  const weeks = [];
  for (let w = 0; w < count; w++) {
    const weekDates = [];
    for (let i = 0; i < 5; i++) {
      weekDates.push(addDays(startMonday, w * 7 + i));
    }
    weeks.push(weekDates);
  }
  return weeks;
}

function getDominantMonth(weeks) {
  const counts = {};
  for (const week of weeks) {
    for (const d of week) {
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      counts[key] = (counts[key] || 0) + 1;
    }
  }
  let best = null;
  let bestCount = 0;
  for (const [key, count] of Object.entries(counts)) {
    if (count > bestCount) {
      bestCount = count;
      best = key;
    }
  }
  const [year, month] = best.split("-").map(Number);
  return { year, month };
}


function sortTasks(tasks) {
  return [...tasks].sort((a, b) => {
    const prioA = PRIO_ORDER[a.prio] ?? 99;
    const prioB = PRIO_ORDER[b.prio] ?? 99;
    if (prioA !== prioB) return prioA - prioB;
    // Manual order within the same prio — this is what drag-reorder persists;
    // sorting by name alone made the whole reorder machinery a visual no-op.
    const soA = a.sort_order ?? 0;
    const soB = b.sort_order ?? 0;
    if (soA !== soB) return soA - soB;
    return (a.name || "").localeCompare(b.name || "", "de");
  });
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [breakpoint]);
  return isMobile;
}

export default function Calendar({ tasks, appointments, roadmapItems = [], employees, areas, onMove, onEdit, onNewTask, onToggle, onReorder, onAddAppointment, onDeleteAppointment, onDuplicateAppointment, onMoveAppointment, onEditAppointment, onQuickUpdate, onDuplicate, onDelete, filters, onFiltersChange, onRangeChange }) {
  // Reactive: rolls over shortly after midnight / on tab focus — a tab left
  // open overnight no longer keeps marking yesterday as "today".
  const todayStr = useToday();
  const today = parseISODateLocal(todayStr);

  const [weekOffset, setWeekOffset] = useState(0); // mobile only
  const [holidays, setHolidays] = useState({});
  const [showMiniCal, setShowMiniCal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [hideTasks, setHideTasks] = useState(false);
  const [showMonthView, setShowMonthView] = useState(false);
  const [scrollTick, setScrollTick] = useState(0);
  const [displayMonth, setDisplayMonth] = useState(today.getMonth()); // 0-indexed
  const [displayYear, setDisplayYear] = useState(today.getFullYear());
  const filterRef = useRef(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const scrollBodyRef = useRef(null);
  const visibleMondayRef = useRef(getMonday(today));
  const mobileScrollRef = useRef(null);
  const isMobile = useIsMobile();

  // Desktop: dynamically extended range (starts with 4 weeks back + 8 weeks ahead)
  const initialDesktopWeeks = useRef(getWeeksFromStart(addDays(getMonday(today), -4 * 7), 13)).current;
  const [renderedWeeks, setRenderedWeeks] = useState(initialDesktopWeeks);
  const isExtendingRef = useRef(false);
  // Mobile: 4 weeks based on weekOffset
  const currentMonday = getMonday(today);
  const startMonday = addDays(currentMonday, (weekOffset - 1) * 7);
  const mobileWeeks = getWeeksFromStart(startMonday, 4);

  // Dedup Outlook copies (one card per synced calendar) and bucket by date.
  // Memoized: this walks every loaded appointment, and the calendar re-renders
  // often (scroll header updates, drag state, mini-cal) with unchanged data.
  const appointmentsByDate = useMemo(() => {
    const byDate = {};
    const outlookDedup = {};
    for (const t of appointments) {
      if (t.outlook_id) {
        const key = `${t.date}|${t.text}|${t.start_time || ""}|${t.end_time || ""}`;
        if (outlookDedup[key]) {
          if (t.user_id && !outlookDedup[key].user_ids.includes(t.user_id)) {
            outlookDedup[key].user_ids.push(t.user_id);
          }
          continue;
        }
        const merged = { ...t, user_ids: t.user_id ? [t.user_id] : [] };
        outlookDedup[key] = merged;
        if (!byDate[t.date]) byDate[t.date] = [];
        byDate[t.date].push(merged);
      } else {
        if (!byDate[t.date]) byDate[t.date] = [];
        byDate[t.date].push(t);
      }
    }
    return byDate;
  }, [appointments]);

  // Build roadmap tags by date (Urlaub, Frei, Betriebsferien)
  // IMPORTANT: parse and iterate dates in local time. Using `new Date("YYYY-MM-DD")`
  // (UTC midnight) and `toISOString()` (UTC key) silently drifts across DST and in
  // timezones with negative offsets — urlaub cards can vanish on boundary days.
  const roadmapByDate = useMemo(() => {
    const byDate = {};
    for (const item of roadmapItems) {
      if (!item.date || !item.date_until) continue;
      const cur = parseISODateLocal(item.date);
      const end = parseISODateLocal(item.date_until);
      while (cur <= end) {
        const key = formatDate(cur);
        if (!byDate[key]) byDate[key] = [];
        byDate[key].push(item);
        cur.setDate(cur.getDate() + 1);
      }
    }
    return byDate;
  }, [roadmapItems]);

  // Year span that needs holidays: the whole desktop range PLUS the mobile
  // window (mobile navigation moves mobileWeeks, not renderedWeeks) — including
  // every year in between: a MiniCal jump across 2+ years used to leave the
  // middle year permanently holiday-less.
  const startYear = Math.min(renderedWeeks[0][0].getFullYear(), mobileWeeks[0][0].getFullYear());
  const endYear = Math.max(renderedWeeks[renderedWeeks.length - 1][4].getFullYear(), mobileWeeks[mobileWeeks.length - 1][4].getFullYear());

  useEffect(() => {
    const years = [];
    for (let y = startYear; y <= endYear; y++) years.push(y);
    Promise.all(years.map((y) => fetchHolidays(y)))
      .then((results) => {
        const merged = {};
        results.forEach((r) => Object.assign(merged, r));
        setHolidays(merged);
      })
      .catch(console.error);
  }, [startYear, endYear]);

  useEffect(() => {
    if (!showFilters) return;
    function handleClickOutside(e) {
      if (filterRef.current && !filterRef.current.contains(e.target) &&
          !e.target.closest(".filter-select-dropdown")) {
        setShowFilters(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilters]);

  // Desktop: initial scroll to the previous week (so the current week is visible)
  useEffect(() => {
    if (isMobile) return;
    const container = scrollBodyRef.current;
    const id = `week-${formatDate(addDays(getMonday(today), -7))}`;
    const el = container?.querySelector(`#${id}`);
    if (el && container) {
      const offset = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
      container.scrollTop = offset;
    }
    // Report initial range
    if (onRangeChange && renderedWeeks.length > 0) {
      const from = formatDate(renderedWeeks[0][0]);
      const to = formatDate(renderedWeeks[renderedWeeks.length - 1][4]);
      onRangeChange(from, to);
    }
  }, []); // once on mount

  // Desktop: update header (month/year) on scroll
  useEffect(() => {
    const container = scrollBodyRef.current;
    if (!container || isMobile) return;
    const handleScroll = () => {
      const containerTop = container.getBoundingClientRect().top;
      const rows = container.querySelectorAll('.calendar-week-row[data-week-start]');
      for (const row of rows) {
        if (row.getBoundingClientRect().bottom > containerTop + 10) {
          const d = new Date(row.dataset.weekStart + 'T00:00:00');
          visibleMondayRef.current = d;
          setDisplayMonth(d.getMonth());
          setDisplayYear(d.getFullYear());
          break;
        }
      }
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Desktop: IntersectionObserver for infinite scroll (extend top/bottom)
  useEffect(() => {
    if (isMobile) return;
    const container = scrollBodyRef.current;
    if (!container) return;

    const observer = new IntersectionObserver((entries) => {
      if (isExtendingRef.current) return;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        if (entry.target === bottomSentinelRef.current) {
          isExtendingRef.current = true;
          setRenderedWeeks(prev => {
            const last = prev[prev.length - 1];
            const newStart = addDays(last[4], 3);
            const newWeeks = getWeeksFromStart(newStart, 8);
            const merged = [...prev, ...newWeeks];
            if (onRangeChange) onRangeChange(formatDate(merged[0][0]), formatDate(merged[merged.length - 1][4]));
            setTimeout(() => { isExtendingRef.current = false; }, 200);
            return merged;
          });
        } else if (entry.target === topSentinelRef.current) {
          isExtendingRef.current = true;
          const scrollHeightBefore = container.scrollHeight;
          setRenderedWeeks(prev => {
            const first = prev[0];
            const newStart = addDays(first[0], -8 * 7);
            const newWeeks = getWeeksFromStart(getMonday(newStart), 8);
            const merged = [...newWeeks, ...prev];
            if (onRangeChange) onRangeChange(formatDate(merged[0][0]), formatDate(merged[merged.length - 1][4]));
            return merged;
          });
          // MutationObserver waits until React has actually rendered the new rows into the DOM
          const mo = new MutationObserver(() => {
            const added = container.scrollHeight - scrollHeightBefore;
            if (added > 0) {
              container.scrollTop += added;
              mo.disconnect();
              setTimeout(() => { isExtendingRef.current = false; }, 200);
            }
          });
          mo.observe(container, { childList: true, subtree: true });
          // Fallback in case MutationObserver doesn't fire
          setTimeout(() => { mo.disconnect(); isExtendingRef.current = false; }, 1000);
        }
      }
    }, { root: container, rootMargin: '200px' });

    if (topSentinelRef.current) observer.observe(topSentinelRef.current);
    if (bottomSentinelRef.current) observer.observe(bottomSentinelRef.current);
    return () => observer.disconnect();
  }, [isMobile, onRangeChange]);


  // Mobile: sync displayMonth/displayYear from weekOffset. isMobile is a dep so
  // that ENTERING mobile mid-session reports the mobile range too.
  useEffect(() => {
    if (!isMobile) return;
    const { year, month } = getDominantMonth(mobileWeeks);
    setDisplayYear(year);
    setDisplayMonth(month);
    // Mobile: report range so tasks get loaded
    if (onRangeChange && mobileWeeks.length > 0) {
      const from = formatDate(mobileWeeks[0][0]);
      const to = formatDate(mobileWeeks[mobileWeeks.length - 1][4]);
      onRangeChange(from, to);
    }
  }, [isMobile, weekOffset]); // mobileWeeks is derived from weekOffset

  // Re-run the initial scroll + range report when the layout switches to
  // desktop mid-session (rotation, window resize): the mount effect ran while
  // mobile had no scroll container, so the grid otherwise starts at the oldest
  // rendered week and the top sentinel immediately prepends more.
  const wasMobileRef = useRef(isMobile);
  useEffect(() => {
    const wasMobile = wasMobileRef.current;
    wasMobileRef.current = isMobile;
    if (!wasMobile || isMobile) return; // only on the mobile -> desktop edge
    const container = scrollBodyRef.current;
    const el = container?.querySelector(`#week-${formatDate(addDays(getMonday(today), -7))}`);
    if (el && container) {
      container.scrollTop = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
    }
    if (onRangeChange && renderedWeeks.length > 0) {
      onRangeChange(formatDate(renderedWeeks[0][0]), formatDate(renderedWeeks[renderedWeeks.length - 1][4]));
    }
  }, [isMobile]); // only the layout edge matters — today/renderedWeeks are read fresh

  // Keyboard: left/right arrow keys navigate like the arrow buttons
  useEffect(() => {
    if (isMobile) return;
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); prevWeek(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); nextWeek(); }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isMobile]); // prevWeek/nextWeek use refs, no re-registration needed

  // Flat list of all days for mobile view
  const allDays = mobileWeeks.flat();

  const scrollToWeek = (monday, behavior = 'smooth') => {
    const container = scrollBodyRef.current;
    const id = `week-${formatDate(monday)}`;
    const el = container?.querySelector(`#${id}`);
    if (el && container) {
      const offset = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
      container.scrollTo({ top: offset, behavior });
    }
  };

  const prevWeek = () => {
    if (isMobile) { setWeekOffset(weekOffset - 1); return; }
    scrollToWeek(addDays(visibleMondayRef.current, -7));
  };
  const nextWeek = () => {
    if (isMobile) { setWeekOffset(weekOffset + 1); return; }
    scrollToWeek(addDays(visibleMondayRef.current, 7));
  };

  const goToday = () => {
    if (isMobile) { goTodayMobile(); return; }
    scrollToWeek(addDays(getMonday(today), -7));
  };

  const scrollToDay = (direction) => {
    const container = mobileScrollRef.current;
    if (!container) return;
    const cards = Array.from(container.children);
    const center = container.scrollLeft + container.offsetWidth / 2;
    let currentIdx = 0;
    for (let i = 0; i < cards.length; i++) {
      if (cards[i].offsetLeft + cards[i].offsetWidth / 2 > center) { currentIdx = i; break; }
    }
    const nextIdx = Math.max(0, Math.min(cards.length - 1, currentIdx + direction));
    const target = cards[nextIdx];
    if (target) {
      container.scrollTo({
        left: target.offsetLeft - (container.offsetWidth - target.offsetWidth) / 2,
        behavior: "smooth",
      });
    }
  };

  const prevDay = () => scrollToDay(-1);
  const nextDay = () => scrollToDay(1);

  const prevMonth = () => {
    const target = new Date(displayYear, displayMonth - 1, 15);
    const mon = getMonday(target);
    if (isMobile) { setWeekOffset(Math.round((mon - currentMonday) / (7 * 86400000))); return; }
    scrollToWeek(mon);
  };
  const nextMonth = () => {
    const target = new Date(displayYear, displayMonth + 1, 15);
    const mon = getMonday(target);
    if (isMobile) { setWeekOffset(Math.round((mon - currentMonday) / (7 * 86400000))); return; }
    scrollToWeek(mon);
  };

  const goTodayMobile = () => {
    if (weekOffset !== 0) {
      mobileScrollTarget.current = todayStr;
      setScrollTick((t) => t + 1);
      setWeekOffset(0);
      return;
    }
    // Already in the right week — scroll directly
    const container = mobileScrollRef.current;
    if (!container) return;
    const todayIdx = allDays.findIndex((d) => formatDate(d) === todayStr);
    let idx;
    if (todayIdx >= 0) {
      idx = todayIdx;
    } else {
      const t = new Date(todayStr + "T00:00:00");
      const fi = allDays.findIndex((d) => d >= t);
      idx = fi >= 0 ? fi : allDays.length - 1;
    }
    const child = container.children[idx];
    if (child) {
      container.scrollTo({
        left: child.offsetLeft - (container.offsetWidth - child.offsetWidth) / 2,
        behavior: "smooth",
      });
    }
  };

  const mobileScrollTarget = useRef(null);
  const topSentinelRef = useRef(null);
  const bottomSentinelRef = useRef(null);

  const handleMiniCalSelect = (year, month, day) => {
    const selectedDate = new Date(year, month, day);
    const selectedMonday = getMonday(selectedDate);
    if (isMobile) {
      const diffMs = selectedMonday - currentMonday;
      const diffWeeks = Math.round(diffMs / (7 * 86400000));
      mobileScrollTarget.current = formatDate(selectedDate);
      setScrollTick((t) => t + 1);
      setWeekOffset(diffWeeks);
    } else {
      const target = addDays(selectedMonday, -7);
      const firstMon = renderedWeeks[0]?.[0];
      const lastMon = renderedWeeks[renderedWeeks.length - 1]?.[0];
      if (firstMon && lastMon && (target < firstMon || target > lastMon)) {
        // Target week isn't rendered yet — extend the range to include it
        // (plus a little context), then scroll once the rows exist. Without
        // this, jumps far into the past/future silently did nothing.
        isExtendingRef.current = true;
        setRenderedWeeks((prev) => {
          let merged = prev;
          if (target < prev[0][0]) {
            const weeksBefore = Math.ceil((prev[0][0] - target) / (7 * 86400000)) + 2;
            merged = [...getWeeksFromStart(getMonday(addDays(prev[0][0], -weeksBefore * 7)), weeksBefore), ...prev];
          } else {
            const lm = prev[prev.length - 1][0];
            const weeksAfter = Math.ceil((target - lm) / (7 * 86400000)) + 2;
            merged = [...prev, ...getWeeksFromStart(addDays(lm, 7), weeksAfter)];
          }
          if (onRangeChange) onRangeChange(formatDate(merged[0][0]), formatDate(merged[merged.length - 1][4]));
          return merged;
        });
        // Retry until the new rows are committed — a fixed delay was too short
        // for wide jumps on slow devices (querySelector found nothing, the
        // jump silently didn't happen).
        const tryScroll = (attempt = 0) => {
          const el = scrollBodyRef.current?.querySelector(`#week-${formatDate(target)}`);
          if (el) {
            scrollToWeek(target, "auto");
            isExtendingRef.current = false;
          } else if (attempt < 20) {
            setTimeout(() => tryScroll(attempt + 1), 50);
          } else {
            isExtendingRef.current = false;
          }
        };
        setTimeout(() => tryScroll(), 50);
      } else {
        scrollToWeek(target);
      }
    }
    setShowMiniCal(false);
  };


  const handleDragStart = (e, task) => {
    dragItem.current = { type: "task", data: task };
    e.dataTransfer.effectAllowed = "move";
    e.target.closest(".task-card").classList.add("dragging");
  };

  const handleAppointmentDragStart = (e, appointment) => {
    dragItem.current = { type: "appointment", data: appointment };
    e.dataTransfer.effectAllowed = "move";
    e.target.closest(".appointment-card").classList.add("dragging");
  };

  const handleAppointmentTagDragStart = (e, appointment) => {
    dragItem.current = { type: "appointment", data: appointment };
    e.dataTransfer.effectAllowed = "move";
    e.target.closest(".appointment-tag-wrap").classList.add("dragging");
  };

  const handleAppointmentTagDragEnd = (e) => {
    const el = e.target.closest(".appointment-tag-wrap");
    if (el) el.classList.remove("dragging");
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    const el = e.target.closest(".task-card") || e.target.closest(".appointment-card");
    if (el) el.classList.remove("dragging");
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDayDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    e.currentTarget.classList.add("drag-over");
  };

  const handleDayDragLeave = (e) => {
    e.currentTarget.classList.remove("drag-over");
  };

  const handleDayDrop = (e, dateStr) => {
    e.preventDefault();
    e.currentTarget.classList.remove("drag-over");
    if (!dragItem.current) return;
    const { type, data } = dragItem.current;
    if (type === "task") {
      if (data.calendar_date !== dateStr) {
        onMove(data.id, dateStr);
      }
    } else if (type === "appointment") {
      if (data.date !== dateStr) {
        onMoveAppointment(data.id, dateStr);
      }
    }
    dragItem.current = null;
  };

  const handleTaskDragOver = (e, task) => {
    // Only claim the drag when a same-day/same-prio reorder is possible.
    // Anything else must bubble up to the day cell, so dropping ON a card
    // still moves the dragged item to that day (it used to be swallowed).
    if (!dragItem.current || dragItem.current.type !== "task") return;
    const src = dragItem.current.data;
    if (src.id === task.id) { e.preventDefault(); e.stopPropagation(); return; }
    if (src.calendar_date === task.calendar_date && src.prio === task.prio) {
      e.preventDefault();
      e.stopPropagation();
      dragOverItem.current = task;
      e.currentTarget.classList.add("drag-over-task");
    }
  };

  const handleTaskDragLeave = (e) => {
    e.currentTarget.classList.remove("drag-over-task");
  };

  const handleTaskDrop = (e, targetTask, dayTasks) => {
    e.currentTarget.classList.remove("drag-over-task");
    // Not a same-day/same-prio reorder? Don't swallow the drop — bubbling up
    // to the day cell turns it into a normal move-to-this-day.
    if (!dragItem.current || dragItem.current.type !== "task") return;
    const src = dragItem.current.data;
    if (src.calendar_date !== targetTask.calendar_date || src.prio !== targetTask.prio) return;
    e.preventDefault();
    e.stopPropagation();
    if (src.id === targetTask.id) return;

    const samePrioTasks = dayTasks
      .filter((t) => t.prio === src.prio)
      .sort((a, b) => a.sort_order - b.sort_order);

    const reordered = samePrioTasks.filter((t) => t.id !== src.id);
    const targetIdx = reordered.findIndex((t) => t.id === targetTask.id);
    reordered.splice(targetIdx, 0, src);

    const items = reordered.map((t, i) => ({ id: t.id, sort_order: i }));
    onReorder(items);
    dragItem.current = null;
  };

  // Pre-bucketed + pre-sorted per day — filtering and sorting the whole task
  // array per day cell per render was O(days x tasks) on every scroll render.
  const tasksByDate = useMemo(() => {
    const byDate = {};
    for (const t of tasks) {
      if (!t.calendar_date) continue;
      if (!byDate[t.calendar_date]) byDate[t.calendar_date] = [];
      byDate[t.calendar_date].push(t);
    }
    for (const k of Object.keys(byDate)) byDate[k] = sortTasks(byDate[k]);
    return byDate;
  }, [tasks]);
  const getTasksForDate = (dateStr) => tasksByDate[dateStr] || [];

  const handleDayClick = (e, dateStr) => {
    if (!e.currentTarget.contains(e.target)) return;
    if (e.target.closest(".task-card")) return;
    if (e.target.closest(".day-header")) return;
    onNewTask(dateStr);
  };

  const handleHeaderClick = (e, dateStr) => {
    e.stopPropagation();
    if (!e.currentTarget.contains(e.target)) return;
    const el = e.target;
    // Only react to clicks on empty header area (day-header, day-header-top, day-date)
    const isHeaderBg = el.classList.contains("day-header") || el.classList.contains("day-header-top") || el.classList.contains("day-date");
    if (!isHeaderBg) return;
    onAddAppointment(dateStr);
  };

  // On mobile: scroll to target day (selected via MiniCal) or today
  useEffect(() => {
    if (!isMobile || !mobileScrollRef.current) return;
    const targetDateStr = mobileScrollTarget.current || todayStr;
    mobileScrollTarget.current = null;
    const targetIdx = allDays.findIndex((d) => formatDate(d) === targetDateStr);
    let scrollToIdx;
    if (targetIdx >= 0) {
      scrollToIdx = targetIdx;
    } else {
      // Weekend or date not in range: scroll to nearest future weekday
      const target = new Date(targetDateStr + "T00:00:00");
      const futureIdx = allDays.findIndex((d) => d >= target);
      scrollToIdx = futureIdx >= 0 ? futureIdx : allDays.length - 1;
    }
    const container = mobileScrollRef.current;
    const doScroll = () => {
      const child = container.children[scrollToIdx];
      if (child) {
        container.scrollTo({
          left: child.offsetLeft - (container.offsetWidth - child.offsetWidth) / 2,
          behavior: "instant",
        });
      }
    };
    // Double-rAF ensures layout is complete (especially on initial mount after login)
    requestAnimationFrame(() => requestAnimationFrame(doScroll));
  }, [isMobile, weekOffset, allDays.length, todayStr, scrollTick]);

  const isDefaultStatus = filters.status && filters.status.length === 1 && filters.status[0] === "open";
  const hasActiveFilters = (filters.status && filters.status.length > 0 && !isDefaultStatus) || (filters.person && filters.person.length > 0) || (filters.prio && filters.prio.length > 0) || (filters.area && filters.area.length > 0);

  return (
    <div className="calendar">
      <div className="calendar-nav">
        <div className="calendar-nav-left">
          <button className="cal-nav-btn" onClick={isMobile ? (showMonthView ? prevMonth : prevDay) : prevWeek} title={isMobile ? (showMonthView ? "Vorheriger Monat" : "Vorheriger Tag") : "Vorherige Woche"}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg></button>
          <span className="cal-nav-today" onClick={isMobile ? (showMonthView ? goToday : goTodayMobile) : goToday}>Heute</span>
          <div className="month-label-wrap">
            <span className="month-label">{MONTHS_SHORT[displayMonth]} {displayYear}</span>
            <button className="cal-nav-btn" onClick={() => setShowMiniCal(!showMiniCal)}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg></button>
            {showMiniCal && (
              <>
                <div className="mobile-backdrop" onClick={() => setShowMiniCal(false)} />
                <MiniCalendar
                  currentYear={displayYear}
                  currentMonth={displayMonth}
                  onSelect={handleMiniCalSelect}
                  onClose={() => setShowMiniCal(false)}
                />
              </>
            )}
          </div>
          <button className="cal-nav-btn" onClick={isMobile ? (showMonthView ? nextMonth : nextDay) : nextWeek} title={isMobile ? (showMonthView ? "Nächster Monat" : "Nächster Tag") : "Nächste Woche"}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg></button>
        </div>
        <div className="filter-wrap" ref={filterRef}>
          {isMobile ? (
            <button
              className={`btn-filter-toggle ${showMonthView ? "has-filters" : ""}`}
              onClick={() => {
                if (showMonthView) {
                  mobileScrollTarget.current = todayStr;
                  setScrollTick((t) => t + 1);
                  setWeekOffset(0);
                }
                setShowMonthView(!showMonthView);
              }}
              title="Monatsübersicht"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </button>
          ) : (
            <button
              className={`btn-filter-toggle ${hideTasks ? "has-filters" : ""}`}
              onClick={() => setHideTasks(!hideTasks)}
              title={hideTasks ? "Tasks einblenden" : "Tasks ausblenden"}
            >
              {hideTasks ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          )}
          <button
            className={`btn-filter-toggle ${hasActiveFilters ? "has-filters" : ""}`}
            onClick={() => setShowFilters(!showFilters)}
            title="Filter"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            {hasActiveFilters && <span className="filter-dot" />}
          </button>
          {showFilters && (
            <>
              <div className="mobile-backdrop" onClick={() => setShowFilters(false)} />
              <div className="filter-overlay">
                <FilterBar filters={filters} onChange={onFiltersChange} employees={employees} areas={areas} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* ===== MOBILE: month overview or day scroller ===== */}
      {isMobile && showMonthView ? (
        <MobileMonthView
          year={displayYear}
          month={displayMonth}
          todayStr={todayStr}
          holidays={holidays}
          appointmentsByDate={appointmentsByDate}
          roadmapByDate={roadmapByDate}
          employees={employees}
          onAddAppointment={onAddAppointment}
          onSelectDate={(dateStr) => {
            const d = parseISODateLocal(dateStr); // local, not UTC-midnight
            const mon = getMonday(d);
            const diff = Math.round((mon - currentMonday) / (7 * 86400000));
            mobileScrollTarget.current = dateStr;
            setScrollTick((t) => t + 1);
            setWeekOffset(diff);
            setShowMonthView(false);
          }}
        />
      ) : isMobile ? (
        <div className="mobile-day-scroller" ref={mobileScrollRef}>
          {allDays.map((date) => {
            const dateStr = formatDate(date);
            const dayTasks = getTasksForDate(dateStr);
            const isToday = todayStr === dateStr;
            const past = isPast(dateStr) && !isToday;
            const holiday = holidays[dateStr];
            const dayAppointments = appointmentsByDate[dateStr] || [];
            const manualAppointments = dayAppointments.filter((t) => !t.outlook_id);
            const outlookAppointments = dayAppointments.filter((t) => t.outlook_id);
            // Individual vacation now goes through the vacation requests (Roadmap) — hide old appointments
            const allDay = manualAppointments.filter((t) => !t.start_time && APPT_TEMPLATE_PATTERN.test(t.text) && !APPT_INDIVIDUAL_VACATION_PATTERN.test(t.text));
            const allDayTags = allDay.filter((t) => !APPT_VACATION_BLOCK_PATTERN.test(t.text));
            const vacationBlocks = allDay.filter((t) => APPT_VACATION_BLOCK_PATTERN.test(t.text));
            const bodyAppointments = manualAppointments.filter((t) => t.start_time || (!t.start_time && !APPT_TEMPLATE_PATTERN.test(t.text)));
            const outlookAllDay = outlookAppointments.filter((t) => t.is_all_day);
            const outlookTimedRaw = outlookAppointments.filter((t) => !t.is_all_day);
            const allTimed = [...bodyAppointments, ...outlookTimedRaw].sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
            const nowStr = isToday ? new Date().toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", hour12: false }) : null;
            const dayFullyPast = past;
            const dayOfWeek = WEEKDAYS[date.getDay() === 0 ? 6 : date.getDay() - 1];

            return (
              <div
                key={dateStr}
                className={`mobile-day-card ${isToday ? "today" : ""} ${past ? "past" : ""} ${holiday ? "holiday" : ""}`}
                onClick={(e) => handleDayClick(e, dateStr)}
              >
                <div className="mobile-day-label" onClick={(e) => { e.stopPropagation(); onAddAppointment(dateStr); }}>
                  <span className="mobile-day-weekday">{dayOfWeek}</span>
                  <span className="mobile-day-date">{formatDateShort(date)}</span>
                  {holiday && <span className="holiday-name">{holiday}</span>}
                </div>
                <div className="day-header" onClick={(e) => handleHeaderClick(e, dateStr)}>
                  <div className="day-header-top">
                    {allDayTags.map((t) => (
                      <AppointmentTag
                        key={t.id}
                        appointment={t}
                        onDelete={onDeleteAppointment}
                        onEdit={onEditAppointment}
                        onDragStart={handleAppointmentTagDragStart}
                        onDragEnd={handleAppointmentTagDragEnd}
                      />
                    ))}
                    {(roadmapByDate[dateStr] || []).filter(item => item.group_key !== "_events" && item.title !== "Krank" && item.title !== "Urlaub").map((item) => {
                      const persons = (item.persons || []).map((p) => p.abbreviation || p.name).join(", ");
                      const titleShort = { "Krank": "K", "Frei": "F", "Urlaub": "U" }[item.title] || item.title;
                      const label = item.title === "Betriebsferien" ? item.title : `${persons ? persons + "-" : ""}${titleShort}`;
                      return (
                        <span key={`rm-${item.id}`} className="appointment-tag-wrap" style={{ cursor: "default" }} onClick={(e) => e.stopPropagation()}>
                          <span className="appointment-tag roadmap-tag">{label}</span>
                        </span>
                      );
                    })}
                  </div>
                  {!holiday && vacationBlocks.map((t) => (
                    <VacationBlockCard
                      key={`urlaub-${t.id}`}
                      appointment={t}
                      elapsed={dayFullyPast}
                      onDelete={onDeleteAppointment}
                      onEdit={onEditAppointment}
                      onDragStart={handleAppointmentTagDragStart}
                      onDragEnd={handleAppointmentTagDragEnd}
                    />
                  ))}
                  {(roadmapByDate[dateStr] || []).filter(item => !holiday && item.synthetic && item.title === "Urlaub").map((item) => {
                    const firstNames = (item.persons || []).map((p) => (p.name || "").split(/\s+/)[0]).filter(Boolean).join(", ");
                    // Half-day vacation gets a "halb" suffix — but not for working students.
                    const half = item.half_day && !item.working_student;
                    const label = `Urlaub${half ? " halb" : ""}`;
                    return (
                      <div
                        key={`vac-${item.id}`}
                        className={`appointment-card vacation-card${dayFullyPast ? " appointment-elapsed" : ""}`}
                        onClick={(e) => e.stopPropagation()}
                        title={label}
                      >
                        <div className="appointment-card-text">{firstNames ? `${firstNames} ` : ""}{label}</div>
                      </div>
                    );
                  })}
                  {(roadmapByDate[dateStr] || []).filter(item => item.group_key === "_events").map((item) => {
                    const persons = (item.persons || []).map((p) => p.abbreviation || p.name).join(", ");
                    return (
                      <div key={`rme-${item.id}`} className="appointment-card roadmap-event-card" onClick={(e) => e.stopPropagation()}>
                        <div className="appointment-card-text">{item.title}{persons && <span className="outlook-persons">{persons}</span>}</div>
                      </div>
                    );
                  })}
                  {outlookAllDay.map((t) => (
                    <div key={`ol-${t.id}`} className={`appointment-card outlook-card${dayFullyPast ? " appointment-elapsed" : ""}`} title={`${t.text}${t.user_ids && t.user_ids.length ? ` (${t.user_ids.map((uid) => { const emp = employees.find((e) => e.id === uid); return emp ? emp.abbreviation : ""; }).filter(Boolean).join(", ")})` : ""}`} onClick={(e) => e.stopPropagation()}>
                      <div className="appointment-card-text">{t.text}{t.user_ids && t.user_ids.length > 0 && (
                        <span className="outlook-persons">{t.user_ids.map((uid) => { const emp = employees.find((e) => e.id === uid); return emp ? emp.abbreviation : ""; }).filter(Boolean).join(", ")}</span>
                      )}</div>
                    </div>
                  ))}
                  {allTimed.map((t) => {
                    const elapsed = dayFullyPast || (nowStr && t.end_time && t.end_time <= nowStr);
                    return t.outlook_id ? (
                    <div key={`outlook-${t.id}`} className={`appointment-card outlook-card${elapsed ? " appointment-elapsed" : ""}`} title={`${t.start_time && t.end_time ? `${t.start_time} – ${t.end_time}\n` : t.start_time ? `${t.start_time}\n` : ""}${t.text}${t.user_ids && t.user_ids.length ? ` (${t.user_ids.map((uid) => { const emp = employees.find((e) => e.id === uid); return emp ? emp.abbreviation : ""; }).filter(Boolean).join(", ")})` : ""}`} onClick={(e) => e.stopPropagation()}>
                      <div className="appointment-card-time">{t.start_time}</div>
                      <div className="appointment-card-text">{t.text}{t.user_ids && t.user_ids.length > 0 && (
                        <span className="outlook-persons">{t.user_ids.map((uid) => { const emp = employees.find((e) => e.id === uid); return emp ? emp.abbreviation : ""; }).filter(Boolean).join(", ")}</span>
                      )}</div>
                    </div>
                    ) : (
                    <AppointmentCardItem
                      key={`termin-${t.id}`}
                      appointment={t}
                      appointments={appointments}
                      elapsed={elapsed}
                      onDelete={onDeleteAppointment}
                      onDuplicate={onDuplicateAppointment}
                      onEdit={onEditAppointment}
                      onDragStart={handleAppointmentDragStart}
                      onDragEnd={handleDragEnd}
                      employees={employees}
                    />
                    );
                  })}
                </div>
                {!hideTasks && <div className="day-tasks">
                  {dayTasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      dayTasks={dayTasks}
                      employees={employees}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                      onTaskDragOver={handleTaskDragOver}
                      onTaskDragLeave={handleTaskDragLeave}
                      onTaskDrop={handleTaskDrop}
                      onEdit={onEdit}
                      onToggle={onToggle}
                      onQuickUpdate={onQuickUpdate}
                      onDuplicate={onDuplicate}
                      onDelete={onDelete}
                    />
                  ))}
                </div>}
              </div>
            );
          })}
        </div>
      ) : (
        /* ===== DESKTOP: week grid ===== */
        <>
          <div className="calendar-header-row">
            <div className="week-header"></div>
            {WEEKDAYS_MF.map((tag) => (
              <div key={tag} className="day-col-header">{tag}</div>
            ))}
          </div>

          <div className="calendar-scroll-body" ref={scrollBodyRef}>
          <div ref={topSentinelRef} style={{ height: 1 }} />
          {renderedWeeks.map((weekDates) => {
            const week = getISOWeek(weekDates[3]);
            const isCurrentWeek = weekDates.some((d) => formatDate(d) === todayStr);
            // Stable key (the week's Monday): with the array index, a top-prepend
            // shifted every index and React re-rendered ALL rows mid scroll-restore.
            return (
              <div
                key={formatDate(weekDates[0])}
                id={`week-${formatDate(weekDates[0])}`}
                data-week-start={formatDate(weekDates[0])}
                className={`calendar-week-row ${isCurrentWeek ? "current-week" : ""}`}
              >
                <div className="week-cell">
                  <span className="week-number">{week}</span>
                </div>
                {weekDates.map((date) => {
                  const dateStr = formatDate(date);
                  const dayTasks = getTasksForDate(dateStr);
                  const isToday = todayStr === dateStr;
                  const past = isPast(dateStr) && !isToday;
                  const holiday = holidays[dateStr];
                  const dayAppointments = appointmentsByDate[dateStr] || [];
                  const manualAppointments = dayAppointments.filter((t) => !t.outlook_id);
                  const outlookAppointments = dayAppointments.filter((t) => t.outlook_id);
                  // Individual vacation now goes through the vacation requests (Roadmap) — hide old appointments
                  const allDay = manualAppointments.filter((t) => !t.start_time && APPT_TEMPLATE_PATTERN.test(t.text) && !APPT_INDIVIDUAL_VACATION_PATTERN.test(t.text));
                  const allDayTags = allDay.filter((t) => !APPT_VACATION_BLOCK_PATTERN.test(t.text));
                  const vacationBlocks = allDay.filter((t) => APPT_VACATION_BLOCK_PATTERN.test(t.text));
                  const bodyAppointments = manualAppointments.filter((t) => t.start_time || (!t.start_time && !APPT_TEMPLATE_PATTERN.test(t.text)));
                  const outlookAllDay = outlookAppointments.filter((t) => t.is_all_day);
                  const outlookTimedRaw = outlookAppointments.filter((t) => !t.is_all_day);
                  const allTimed = [...bodyAppointments, ...outlookTimedRaw].sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
                  const nowStr = isToday ? new Date().toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", hour12: false }) : null;
                  const dayFullyPast = past;

                  return (
                    <div
                      key={dateStr}
                      className={`calendar-day ${isToday ? "today" : ""} ${past ? "past" : ""} ${holiday ? "holiday" : ""}`}
                      onDragOver={handleDayDragOver}
                      onDragLeave={handleDayDragLeave}
                      onDrop={(e) => handleDayDrop(e, dateStr)}
                      onClick={(e) => handleDayClick(e, dateStr)}
                    >
                      <div className="day-header" onClick={(e) => handleHeaderClick(e, dateStr)}>
                        <div className="day-header-top">
                          <span className="day-date" title="Klick = Termin hinzufügen">{formatDateShort(date)}</span>
                          {allDayTags.map((t) => (
                            <AppointmentTag
                              key={t.id}
                              appointment={t}
                              onDelete={onDeleteAppointment}
                              onEdit={onEditAppointment}
                              onDragStart={handleAppointmentTagDragStart}
                              onDragEnd={handleAppointmentTagDragEnd}
                            />
                          ))}
                          {(roadmapByDate[dateStr] || []).filter(item => item.group_key !== "_events" && item.title !== "Krank" && item.title !== "Urlaub").map((item) => {
                            const persons = (item.persons || []).map((p) => p.abbreviation || p.name).join(", ");
                            const titleShort = { "Krank": "K", "Frei": "F", "Urlaub": "U" }[item.title] || item.title;
                            const label = item.title === "Betriebsferien" ? item.title : `${persons ? persons + "-" : ""}${titleShort}`;
                            return (
                              <span key={`rm-${item.id}`} className="appointment-tag-wrap" style={{ cursor: "default" }} onClick={(e) => e.stopPropagation()}>
                                <span className="appointment-tag roadmap-tag">{label}</span>
                              </span>
                            );
                          })}
                          {holiday && <span className="holiday-name" title={holiday}>{holiday}</span>}
                        </div>
                        {!holiday && vacationBlocks.map((t) => (
                          <VacationBlockCard
                            key={`urlaub-${t.id}`}
                            appointment={t}
                            elapsed={dayFullyPast}
                            onDelete={onDeleteAppointment}
                            onEdit={onEditAppointment}
                            onDragStart={handleAppointmentTagDragStart}
                            onDragEnd={handleAppointmentTagDragEnd}
                          />
                        ))}
                        {(roadmapByDate[dateStr] || []).filter(item => !holiday && item.synthetic && item.title === "Urlaub").map((item) => {
                          const firstNames = (item.persons || []).map((p) => (p.name || "").split(/\s+/)[0]).filter(Boolean).join(", ");
                          const half = item.half_day && !item.working_student;
                          const label = `Urlaub${half ? " halb" : ""}`;
                          return (
                            <div
                              key={`vac-${item.id}`}
                              className={`appointment-card vacation-card${dayFullyPast ? " appointment-elapsed" : ""}`}
                              onClick={(e) => e.stopPropagation()}
                              title={label}
                            >
                              <div className="appointment-card-text">{firstNames ? `${firstNames} ` : ""}{label}</div>
                            </div>
                          );
                        })}
                        {(roadmapByDate[dateStr] || []).filter(item => item.group_key === "_events").map((item) => {
                          const persons = (item.persons || []).map((p) => p.abbreviation || p.name).join(", ");
                          return (
                            <div key={`rme-${item.id}`} className="appointment-card roadmap-event-card" onClick={(e) => e.stopPropagation()}>
                              <div className="appointment-card-text">{item.title}{persons && <span className="outlook-persons">{persons}</span>}</div>
                            </div>
                          );
                        })}
                        {outlookAllDay.map((t) => (
                          <div key={`ol-${t.id}`} className={`appointment-card outlook-card${dayFullyPast ? " appointment-elapsed" : ""}`} title={`${t.text}${t.user_ids && t.user_ids.length ? ` (${t.user_ids.map((uid) => { const emp = employees.find((e) => e.id === uid); return emp ? emp.abbreviation : ""; }).filter(Boolean).join(", ")})` : ""}`} onClick={(e) => e.stopPropagation()}>
                            <div className="appointment-card-text">{t.text}{t.user_ids && t.user_ids.length > 0 && (
                              <span className="outlook-persons">{t.user_ids.map((uid) => { const emp = employees.find((e) => e.id === uid); return emp ? emp.abbreviation : ""; }).filter(Boolean).join(", ")}</span>
                            )}</div>
                          </div>
                        ))}
                        {allTimed.map((t) => {
                          const elapsed = dayFullyPast || (nowStr && t.end_time && t.end_time <= nowStr);
                          return t.outlook_id ? (
                          <div key={`outlook-${t.id}`} className={`appointment-card outlook-card${elapsed ? " appointment-elapsed" : ""}`} title={`${t.start_time && t.end_time ? `${t.start_time} – ${t.end_time}\n` : t.start_time ? `${t.start_time}\n` : ""}${t.text}${t.user_ids && t.user_ids.length ? ` (${t.user_ids.map((uid) => { const emp = employees.find((e) => e.id === uid); return emp ? emp.abbreviation : ""; }).filter(Boolean).join(", ")})` : ""}`} onClick={(e) => e.stopPropagation()}>
                            {t.start_time && <div className="appointment-card-time">{t.start_time}</div>}
                            <div className="appointment-card-text">{t.text}{t.user_ids && t.user_ids.length > 0 && (
                              <span className="outlook-persons">{t.user_ids.map((uid) => { const emp = employees.find((e) => e.id === uid); return emp ? emp.abbreviation : ""; }).filter(Boolean).join(", ")}</span>
                            )}</div>
                          </div>
                          ) : (
                          <AppointmentCardItem
                            key={`termin-${t.id}`}
                            appointment={t}
                            appointments={appointments}
                            elapsed={elapsed}
                            onDelete={onDeleteAppointment}
                            onDuplicate={onDuplicateAppointment}
                            onEdit={onEditAppointment}
                            onDragStart={handleAppointmentDragStart}
                            onDragEnd={handleDragEnd}
                            employees={employees}
                          />
                          );
                        })}
                      </div>
                      {!hideTasks && <div className="day-tasks">
                        {dayTasks.map((task) => (
                          <TaskCard
                            key={task.id}
                            task={task}
                            dayTasks={dayTasks}
                            employees={employees}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            onTaskDragOver={handleTaskDragOver}
                            onTaskDragLeave={handleTaskDragLeave}
                            onTaskDrop={handleTaskDrop}
                            onEdit={onEdit}
                            onToggle={onToggle}
                            onQuickUpdate={onQuickUpdate}
                            onDuplicate={onDuplicate}
                            onDelete={onDelete}
                          />
                        ))}
                      </div>}
                    </div>
                  );
                })}
              </div>
            );
          })}
          <div ref={bottomSentinelRef} style={{ height: 1 }} />
          </div>
        </>
      )}

    </div>
  );
}

function TaskCard({ task, dayTasks, employees, onDragStart, onDragEnd, onTaskDragOver, onTaskDragLeave, onTaskDrop, onEdit, onToggle, onQuickUpdate, onDuplicate, onDelete }) {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const areaColor = AREA_COLORS[task.area] || { bg: "#f3f4f6", border: "#9ca3af", text: "#374151" };
  const persons = [...(task.persons || [])].sort((a, b) =>
    (a.abbreviation || a.name || "").localeCompare(b.abbreviation || b.name || "")
  );

  const handleClick = (e) => {
    if (e.target.closest(".task-check")) return;
    if (e.target.closest(".task-dots-wrap")) return;
    if (!e.currentTarget.contains(e.target)) return;
    onEdit(task);
  };

  const handleDotsClick = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, left: rect.right });
    setShowMenu(!showMenu);
  };

  return (
    <div
      className={`task-card ${task.completed ? "done" : ""}`}
      draggable
      onDragStart={(e) => onDragStart(e, task)}
      onDragEnd={onDragEnd}
      onDragOver={(e) => onTaskDragOver(e, task)}
      onDragLeave={onTaskDragLeave}
      onDrop={(e) => onTaskDrop(e, task, dayTasks)}
      onClick={handleClick}
      onMouseLeave={() => { if (showMenu && !confirmDelete) setShowMenu(false); }}
      title={task.name}
      style={{
        background: "#2e3240",
        borderColor: areaColor.border,
      }}
    >
      <div className="task-main-row">
        <button
          className={`task-check ${task.completed ? "checked" : ""}`}
          onClick={(e) => { e.stopPropagation(); onToggle(task.id); }}
          title={task.completed ? "Als offen markieren" : "Abhaken"}
        >
          {task.completed && <span className="checkmark">✓</span>}
        </button>
        <div className="task-content">
          <div className="task-name-row">
            <span className="task-name">{task.name}</span>
            <div className={`task-dots-wrap ${showMenu ? "menu-open" : ""}`}>
              <button className="task-dots-btn" onClick={handleDotsClick} title="Aktionen">⋯</button>
              {showMenu && menuPos && (
                <TaskQuickMenu
                  task={task}
                  employees={employees}
                  onUpdate={onQuickUpdate}
                  onDuplicate={onDuplicate}
                  onDelete={() => { setShowMenu(false); setConfirmDelete(true); }}
                  onClose={() => setShowMenu(false)}
                  pos={menuPos}
                />
              )}
            </div>
            <span className="task-prio-icon" title={task.prio}><PrioIcon prio={task.prio} size={10} /></span>
            {persons.length > 0 ? (
              persons.map((p) => (
                <div key={p.id} className="task-avatar" style={{ background: p.color, color: "white" }} title={p.name}>
                  {getInitials(p.abbreviation || p.name)}
                </div>
              ))
            ) : (
              <div className="task-avatar task-avatar-empty" />
            )}
          </div>
          {task.deadline && (
            <div className="task-info-row">
              <span className="task-deadline">DL {task.deadline.split("-").reverse().slice(0, 2).join(".")}.</span>
            </div>
          )}
        </div>
      </div>
      {confirmDelete && (
        <ConfirmDialog
          message={`Task "${task.name}" wirklich löschen?`}
          onConfirm={() => { onDelete(task.id); setConfirmDelete(false); }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}

function AppointmentMenu({ appointment, appointments, onDelete, onDuplicate, onClose, pos }) {
  const ref = useRef(null);
  const isVacationBlock = APPT_VACATION_BLOCK_PATTERN.test(appointment.text);
  const relatedAppointments = isVacationBlock ? appointments.filter((t) => t.text === appointment.text) : [];
  const [confirmState, setConfirmState] = useState(null);
  // Accurate contiguous block (fetched fresh) — loaded once the dialog opens.
  const [blockDays, setBlockDays] = useState(null);
  useEffect(() => {
    if (confirmState !== "block") { setBlockDays(null); return; }
    let alive = true;
    collectBlockDays(appointment).then((run) => { if (alive) setBlockDays(run); });
    return () => { alive = false; };
  }, [confirmState, appointment]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  if (confirmState === "block") {
    return ReactDOM.createPortal(
      <div className="confirm-overlay" onClick={(e) => { e.stopPropagation(); onClose(); }}>
        <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
          <p className="confirm-message">{`"${appointment.text}" — was möchtest du tun?`}</p>
          <div className="confirm-actions confirm-actions-col">
            <button className="btn btn-danger btn-sm" disabled={!blockDays} onClick={() => { (blockDays || []).forEach((t) => onDelete(t.id)); onClose(); }}>Komplett löschen ({blockDays ? `${blockDays.length} Tage` : "…"})</button>
            <button className="btn btn-warning btn-sm" onClick={() => { onDelete(appointment.id); onClose(); }}>Nur diesen Tag entfernen</button>
            <button className="btn btn-secondary btn-sm" onClick={onClose}>Abbrechen</button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  if (confirmState === "single") {
    return ReactDOM.createPortal(
      <ConfirmDialog
        message={`Termin "${appointment.text}" löschen?`}
        onConfirm={() => { onDelete(appointment.id); onClose(); }}
        onCancel={onClose}
      />,
      document.body
    );
  }

  return ReactDOM.createPortal(
    <div className="quick-menu" ref={ref} style={{ top: pos.top, left: pos.left, transform: 'translateX(-100%)' }} onClick={(e) => e.stopPropagation()}>
      <button className="quick-menu-item" onClick={() => { onDuplicate(appointment); onClose(); }}>
        <span className="quick-menu-icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
        </span>
        Duplizieren
      </button>
      <button className="quick-menu-item quick-menu-danger" onClick={() => {
        if (isVacationBlock && relatedAppointments.length > 1) {
          setConfirmState("block");
        } else {
          setConfirmState("single");
        }
      }}>
        <span className="quick-menu-icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
        </span>
        Löschen
      </button>
    </div>,
    document.body
  );
}

function formatTagDisplay(text) {
  const match = text.match(APPT_OFFICE_PATTERN);
  if (match) {
    // "AB Arbeit" → "AB-A", "AB Arbeit VM" → "AB-AV", "AB Office NM" → "AB-ON"
    return `${match[1]}-${match[2][0]}${match[3] ? match[3][0] : ""}`;
  }
  return text;
}

// Office/Arbeit tags only — vacation blocks render as VacationBlockCard below,
// so the tag needs nothing but a plain single-day delete.
function AppointmentTag({ appointment, onDelete, onEdit, onDragStart, onDragEnd }) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmDelete(true);
  };

  return (
    <span
      className="appointment-tag-wrap"
      onClick={(e) => { e.stopPropagation(); if (onEdit) onEdit(appointment); }}
      onContextMenu={handleContextMenu}
      draggable
      onDragStart={(e) => onDragStart(e, appointment)}
      onDragEnd={onDragEnd}
      style={onEdit ? { cursor: "pointer" } : {}}
      title={`${appointment.start_time && appointment.end_time ? `${appointment.start_time} – ${appointment.end_time}\n` : ""}Klick zum Bearbeiten, Rechtsklick zum Löschen`}
    >
      <span className="appointment-tag">{formatTagDisplay(appointment.text)}</span>
      {confirmDelete && ReactDOM.createPortal(
        <ConfirmDialog
          message={`Termin "${appointment.text}" löschen?`}
          onConfirm={() => { onDelete(appointment.id); setConfirmDelete(false); }}
          onCancel={() => setConfirmDelete(false)}
        />,
        document.body
      )}
    </span>
  );
}

/* Ganztägiger Urlaubs-/Betriebsferien-Block als Karte. Rechtsklick öffnet den
   Block-Dialog ("Komplett löschen") — nach dem Tag/Karten-Split hing der nur
   noch am dafür unerreichbaren AppointmentTag, sodass Betriebsferien-Blöcke
   nur noch Tag für Tag löschbar waren. */
function VacationBlockCard({ appointment, elapsed, onDelete, onEdit, onDragStart, onDragEnd }) {
  const [showConfirm, setShowConfirm] = useState(false);
  // Accurate contiguous block (fetched fresh) — loaded once the dialog opens.
  const [blockDays, setBlockDays] = useState(null);
  useEffect(() => {
    if (!showConfirm) { setBlockDays(null); return; }
    let alive = true;
    collectBlockDays(appointment).then((run) => { if (alive) setBlockDays(run); });
    return () => { alive = false; };
  }, [showConfirm, appointment]);

  return (
    <div
      className={`appointment-card vacation-card${elapsed ? " appointment-elapsed" : ""}`}
      onClick={(e) => { e.stopPropagation(); if (onEdit) onEdit(appointment); }}
      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setShowConfirm(true); }}
      draggable
      onDragStart={(e) => onDragStart(e, appointment)}
      onDragEnd={onDragEnd}
      title={`${appointment.text} – Klick zum Bearbeiten, Rechtsklick zum Löschen`}
    >
      <div className="appointment-card-text">{appointment.text}</div>
      {showConfirm && ReactDOM.createPortal(
        <div className="confirm-overlay" onClick={(e) => { e.stopPropagation(); setShowConfirm(false); }}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <p className="confirm-message">{`"${appointment.text}" — was möchtest du tun?`}</p>
            <div className="confirm-actions confirm-actions-col">
              <button className="btn btn-danger btn-sm" disabled={!blockDays} onClick={() => { (blockDays || []).forEach((t) => onDelete(t.id)); setShowConfirm(false); }}>Komplett löschen ({blockDays ? `${blockDays.length} Tage` : "…"})</button>
              <button className="btn btn-warning btn-sm" onClick={() => { onDelete(appointment.id); setShowConfirm(false); }}>Nur diesen Tag entfernen</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowConfirm(false)}>Abbrechen</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function AppointmentCardItem({ appointment, appointments, elapsed, onDelete, onDuplicate, onEdit, onDragStart, onDragEnd, employees }) {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPos, setMenuPos] = useState(null);

  const handleDotsClick = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, left: rect.right });
    setShowMenu(!showMenu);
  };

  const handleClick = (e) => {
    if (e.target.closest(".appointment-card-dots-btn")) return;
    if (!e.currentTarget.contains(e.target)) return;
    if (onEdit) onEdit(appointment);
  };

  const personsLabel = (appointment.person_ids && appointment.person_ids.length > 0 && employees)
    ? appointment.person_ids.map((uid) => { const emp = employees.find((e) => e.id === uid); return emp ? emp.abbreviation : ""; }).filter(Boolean).join(", ")
    : "";

  return (
    <div
      className={`appointment-card${elapsed ? " appointment-elapsed" : ""}`}
      title={`${appointment.start_time && appointment.end_time ? `${appointment.start_time} – ${appointment.end_time}\n` : appointment.start_time ? `${appointment.start_time}\n` : ""}${appointment.text}${personsLabel ? ` (${personsLabel})` : ""}`}
      draggable
      onDragStart={(e) => onDragStart(e, appointment)}
      onDragEnd={onDragEnd}
      onClick={handleClick}
    >
      {appointment.start_time && (
        <div className="appointment-card-time">{appointment.start_time}</div>
      )}
      <div className="appointment-card-text">{appointment.text}{personsLabel && <span className="outlook-persons">{personsLabel}</span>}</div>
      <button className="appointment-card-dots-btn" onClick={handleDotsClick} title="Aktionen">⋯</button>
      {showMenu && menuPos && (
        <AppointmentMenu
          appointment={appointment}
          appointments={appointments}
          onDelete={onDelete}
          onDuplicate={onDuplicate}
          onClose={() => setShowMenu(false)}
          pos={menuPos}
        />
      )}
    </div>
  );
}

function MobileMonthView({ year, month, todayStr, holidays, appointmentsByDate, roadmapByDate, employees, onAddAppointment, onSelectDate }) {
  const WEEKDAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr"];

  // Build all weekdays (Mon-Fri) of the month
  const lastDay = new Date(year, month + 1, 0);

  // Find the first weekday (Mon-Fri) of the month
  let firstWeekday = new Date(year, month, 1);
  while (firstWeekday.getDay() === 0 || firstWeekday.getDay() === 6) {
    firstWeekday.setDate(firstWeekday.getDate() + 1);
  }
  // Offset: position of first weekday within Mon-Fri row (Mon=0 ... Fri=4)
  const startOffset = (firstWeekday.getDay() + 6) % 7; // Mon=0

  const days = [];
  for (let i = 0; i < startOffset; i++) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) {
    const date = new Date(year, month, d);
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) continue;
    const dateStr = formatDate(date);
    days.push({ date, dateStr, day: d });
  }

  return (
    <div className="mobile-month-view">
      <div className="mobile-month-weekdays">
        {WEEKDAY_LABELS.map((w) => <span key={w} className="mobile-month-wd">{w}</span>)}
      </div>
      <div className="mobile-month-grid">
        {days.map((item, i) => {
          if (!item) return <div key={`empty-${i}`} className="mobile-month-cell empty" />;
          const { dateStr, day } = item;
          const isToday = dateStr === todayStr;
          const holiday = holidays[dateStr];
          const dayAppts = appointmentsByDate[dateStr] || [];
          const roadmapItems = roadmapByDate[dateStr] || [];
          // Hide office/work templates AND old individual "XX Urlaub" tags — the
          // day/week views hide the latter too (vacation lives in the requests).
          const hideInMonth = (text) => APPT_OFFICE_PATTERN.test(text) || APPT_INDIVIDUAL_VACATION_PATTERN.test(text);
          const tags = dayAppts.filter((t) => !t.outlook_id && !t.start_time && APPT_TEMPLATE_PATTERN.test(t.text) && !hideInMonth(t.text));
          const normalAppts = dayAppts.filter((t) => !t.outlook_id && (t.start_time || (!t.start_time && !APPT_TEMPLATE_PATTERN.test(t.text))));
          const outlookAppts = dayAppts.filter((t) => t.outlook_id);

          return (
            <div
              key={dateStr}
              className={`mobile-month-cell ${isToday ? "today" : ""} ${holiday ? "holiday" : ""}`}
              onClick={() => onSelectDate(dateStr)}
            >
              <span className="mobile-month-day-num">{day}</span>
              {holiday && <span className="mobile-month-holiday">{holiday}</span>}
              {tags.map((t) => (
                <span key={t.id} className="mobile-month-appt tag">{t.text}</span>
              ))}
              {roadmapItems.map((ri) => {
                const persons = (ri.persons || []).map((p) => p.abbreviation || p.name).join(", ");
                const titleShort = { "Krank": "K", "Frei": "F", "Urlaub": "U" }[ri.title] || ri.title;
                const label = ri.title === "Betriebsferien" ? ri.title : `${persons ? persons + "-" : ""}${titleShort}`;
                return <span key={`rm-${ri.id}-${dateStr}`} className="mobile-month-appt roadmap">{label}</span>;
              })}
              {normalAppts.map((t) => (
                <span key={t.id} className="mobile-month-appt normal">
                  {t.start_time ? `${t.start_time} ` : ""}{t.text}
                </span>
              ))}
              {outlookAppts.map((t) => (
                <span key={t.id} className="mobile-month-appt outlook">
                  {t.start_time ? `${t.start_time} ` : ""}{t.text}
                </span>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
